// buildOverview — pure construction of the Trip_Overview_Card summary model.
//
// Transforms a trip object plus the timeline's derived step statuses into the
// at-a-glance overview shown above the phase containers: traveler, destination,
// departure / return dates, outbound / return flight labels, hotel, and the two
// badges (trip status + trip type).
//
// Every summary field is populated either from trip data or a defined
// placeholder so the card never renders an empty cell. The `statusBadge.label`
// is one of {In progress, Upcoming, Completed} derived from the step statuses,
// and the `typeBadge.label` is "Round trip" when a later flight leg returns to
// the journey origin, otherwise "One way".
//
// The function is total — it never throws for any trip shape (missing flights,
// null trip, malformed dates) and falls back to placeholders instead.
//
// Validates: Requirements 1.2, 1.4, 1.5

/**
 * @typedef {Object} OverviewBadge
 * @property {'blue'|'green'|'red'|'grey'} color
 * @property {string} label
 *
 * @typedef {Object} TripOverview
 * @property {string} traveler
 * @property {string} destination
 * @property {string} departureDate
 * @property {string} returnDate
 * @property {string} outboundFlight
 * @property {string} returnFlight
 * @property {string} hotel
 * @property {OverviewBadge} statusBadge
 * @property {OverviewBadge} typeBadge
 */

// ── Constants ───────────────────────────────────────────────

// Single defined placeholder used for any field absent from the trip data, so
// the overview card always renders a value (Req 1.2).
const PLACEHOLDER = 'Not provided';

// Status label → badge color (blue→info, grey→neutral, green→success), per the
// design's badge color map (Req 1.4).
const STATUS_BADGE_COLORS = {
  'In progress': 'blue',
  Upcoming: 'grey',
  Completed: 'green',
};

// ── Helpers ─────────────────────────────────────────────────

/**
 * Returns the value when it is a non-empty (post-trim) string, else null.
 *
 * @param {*} value
 * @returns {string|null}
 */
function nonEmpty(value) {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/**
 * Parses a date value into a Date object safely.
 *
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
function safeParseDate(value) {
  if (value == null) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Formats a date value as "Mon D, YYYY" (e.g. "Jun 15, 2025"), or null when the
 * value is missing or invalid.
 *
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
function formatDate(value) {
  const d = safeParseDate(value);
  if (!d) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Normalizes an airport code to a trimmed uppercase string, or null.
 *
 * @param {*} code
 * @returns {string|null}
 */
function normalizeCode(code) {
  return typeof code === 'string' && code.trim() !== ''
    ? code.trim().toUpperCase()
    : null;
}

/**
 * Builds a flight display label like "AA123 · AUS → JFK", degrading gracefully
 * when the number or route is partially missing. Returns null when nothing can
 * be shown.
 *
 * @param {Object|null} flight
 * @returns {string|null}
 */
function formatFlightLabel(flight) {
  if (!flight || typeof flight !== 'object') return null;

  const number = nonEmpty(flight.flightNumber) || nonEmpty(flight.flightId);
  const dep = normalizeCode(flight.departureAirport);
  const arr = normalizeCode(flight.arrivalAirport);
  const route = dep && arr ? `${dep} → ${arr}` : dep || arr || null;

  if (number && route) return `${number} · ${route}`;
  return number || route || null;
}

/**
 * Finds the flight leg that returns to the journey origin, if any. The origin
 * is the first leg's departure airport; a return leg is a later leg whose
 * arrival airport matches that origin. The latest such leg is returned so
 * multi-leg round trips resolve to the final homeward flight.
 *
 * @param {Array<Object>} flights
 * @returns {Object|null}
 */
function findReturnLeg(flights) {
  if (flights.length < 2) return null;

  const origin = normalizeCode(flights[0]?.departureAirport);
  if (!origin) return null;

  for (let i = flights.length - 1; i >= 1; i -= 1) {
    if (normalizeCode(flights[i]?.arrivalAirport) === origin) {
      return flights[i];
    }
  }
  return null;
}

/**
 * Derives the trip-status label from the timeline's per-step statuses:
 *   - "Completed" when every step is completed
 *   - "Upcoming"  when every step is upcoming
 *   - "In progress" otherwise (any step in-progress, or a mix of completed and
 *     upcoming steps — the journey has started but not finished)
 * With no steps available, the trip is treated as "Upcoming".
 *
 * @param {Array<{status?: string}>} derivedStatuses
 * @returns {'In progress'|'Upcoming'|'Completed'}
 */
function deriveStatusLabel(derivedStatuses) {
  const steps = Array.isArray(derivedStatuses) ? derivedStatuses : [];
  const statuses = steps.map((s) => s && s.status);

  if (statuses.length === 0) return 'Upcoming';
  if (statuses.every((s) => s === 'completed')) return 'Completed';
  if (statuses.every((s) => s === 'upcoming')) return 'Upcoming';
  return 'In progress';
}

/**
 * Resolve the traveler display value, falling back to the placeholder.
 *
 * @param {Object} safeTrip
 * @returns {string}
 */
function resolveTraveler(safeTrip) {
  return (
    nonEmpty(safeTrip.travelerName) || nonEmpty(safeTrip.traveler) || PLACEHOLDER
  );
}

/**
 * Resolve the destination, preferring explicit trip data then the outbound
 * leg's arrival airport, else the placeholder.
 *
 * @param {Object} safeTrip
 * @param {Object|null} outboundLeg
 * @returns {string}
 */
function resolveDestination(safeTrip, outboundLeg) {
  return (
    nonEmpty(safeTrip.destination) ||
    (outboundLeg && normalizeCode(outboundLeg.arrivalAirport)) ||
    PLACEHOLDER
  );
}

/**
 * Resolve the departure date label from the outbound leg or the trip start.
 *
 * @param {Object} safeTrip
 * @param {Object|null} outboundLeg
 * @returns {string}
 */
function resolveDepartureDate(safeTrip, outboundLeg) {
  return (
    formatDate(outboundLeg && outboundLeg.departureTime) ||
    formatDate(safeTrip.startDate) ||
    PLACEHOLDER
  );
}

/**
 * Resolve the return date label from the return leg or the trip end.
 *
 * @param {Object} safeTrip
 * @param {Object|null} returnLeg
 * @returns {string}
 */
function resolveReturnDate(safeTrip, returnLeg) {
  return (
    formatDate(returnLeg && returnLeg.departureTime) ||
    formatDate(safeTrip.endDate) ||
    PLACEHOLDER
  );
}

/**
 * Resolve the hotel name from the first hotel, else the placeholder.
 *
 * @param {Array<Object>} hotels
 * @returns {string}
 */
function resolveHotel(hotels) {
  return nonEmpty(hotels[0] && hotels[0].name) || PLACEHOLDER;
}

/**
 * Build the trip-status badge from the derived per-step statuses.
 *
 * @param {Array<{status?: string}>} derivedStatuses
 * @returns {OverviewBadge}
 */
function buildStatusBadge(derivedStatuses) {
  const statusLabel = deriveStatusLabel(derivedStatuses);
  return { color: STATUS_BADGE_COLORS[statusLabel], label: statusLabel };
}

// ── Main ────────────────────────────────────────────────────

/**
 * Constructs the Trip_Overview_Card view model.
 *
 * @param {Object} trip - Full trip object (flights, hotels, traveler, dates).
 * @param {Array<{status?: string}>} derivedStatuses - Steps with assigned
 *        statuses (output of assignStepStatuses), used to derive the trip's
 *        status badge.
 * @returns {TripOverview}
 */
export function buildOverview(trip, derivedStatuses) {
  const safeTrip = trip && typeof trip === 'object' ? trip : {};
  const flights = Array.isArray(safeTrip.flights) ? safeTrip.flights : [];
  const hotels = Array.isArray(safeTrip.hotels) ? safeTrip.hotels : [];

  const outboundLeg = flights.length > 0 ? flights[0] : null;
  const returnLeg = findReturnLeg(flights);

  return {
    traveler: resolveTraveler(safeTrip),
    destination: resolveDestination(safeTrip, outboundLeg),
    departureDate: resolveDepartureDate(safeTrip, outboundLeg),
    returnDate: resolveReturnDate(safeTrip, returnLeg),
    outboundFlight: formatFlightLabel(outboundLeg) || PLACEHOLDER,
    returnFlight: formatFlightLabel(returnLeg) || PLACEHOLDER,
    hotel: resolveHotel(hotels),
    statusBadge: buildStatusBadge(derivedStatuses),
    typeBadge: { color: 'grey', label: returnLeg ? 'Round trip' : 'One way' },
  };
}

export default buildOverview;
